import React, {useCallback, useContext, useEffect, useMemo, useState} from "react";
import {Collapse, ConfigProvider, message, Modal} from "antd";
import {set} from "lodash";
import {NOOP} from "../../utils/NOOP.jsx";
import {AllowedSystemIdPaymentType} from "../../consts.js";
import {StyledButton} from "../../components/index.jsx";
import {StringBuilder} from "../../AppUtils.js";
import {getThemeByOrigin} from "../../utils.js";
import {validateBooking} from "./utils.jsx";
import {OrderFormPanelKeys} from "../../marketplace/consts.jsx";
import {PaymentPanel} from "../../marketplace/MarketplaceBooking/Payment/PaymentPanel.jsx";
import {MarketplaceBookingContext} from "../../marketplace/MarketplaceBooking/MarketplaceBookingContext.jsx";
import {MarketplaceContext} from "../../marketplace/marketplaceContext";
import {BOOKING_ERROR_TO_PANEL, BOOKING_TO_PANEL_COLORS} from "../../marketplace/MarketplaceBooking/consts.jsx";
import {expandIconController, MarketplaceSeparatorLine} from "../../marketplace/MarketplaceHelper.jsx";
import {OrderForm} from "../../marketplace/OrderForm.jsx";
import classnames from "classnames";
import PageTitleHeader from "../../components/PageTitle.jsx";
import {getTotalBudget} from "../../utils/calculationUtils.jsx";

export const BidPaymentModal = ({
    visible,
    onClose,
    onSave = NOOP,
    totalBidAmount,
    isLoading,
    me = {},
    company = {},
    services = [],
    brief = {},
    bid = {},
    log = NOOP
}) => {
    const [formValues, setFormValues] = useState({});
    const [paymentType, setPaymentType] = useState(null);
    const [creditCard, setCreditCard] = useState(false);
    const [activeKey, setActiveKey] = useState(OrderFormPanelKeys.Payment);
    const {highlightOrderPanel, setHighlightOrderPanel, product, providerDetails} = useContext(MarketplaceContext);

    const onFormValueChange = useCallback(
        (field, val) => setFormValues(prevValues => set({...prevValues}, field, val)),
        [setFormValues]
    );

    const closeHandler = useCallback(() => {
        setFormValues({});
        setHighlightOrderPanel(null);
        onClose();
    }, [onClose]);

    const totalLoadRequestBudgetAmount = useMemo(() => {
        const totalBudget = getTotalBudget(me?.budget, [brief?.serviceId]);

        if (totalBidAmount > totalBudget) {
            return Math.ceil(totalBidAmount - totalBudget);
        }

        return 0;
    }, [totalBidAmount, me?.budget, brief?.serviceId]);

    const bookingError = useMemo(
        () =>
            validateBooking(me, paymentType, bid?.price, company, formValues, creditCard, bid?.deliveryPrice, 0, 0, 0, [
                brief?.serviceId
            ]),
        [me, paymentType, bid?.price, company, formValues, creditCard, bid?.deliveryPrice, brief?.serviceId]
    );

    useEffect(() => {
        if (!bookingError) {
            if (!BOOKING_ERROR_TO_PANEL[bookingError]) {
                setHighlightOrderPanel(null);
            }
        }
    }, [bookingError]);

    const onConfirmOrderPayment = useCallback(() => {
        if (!!bookingError) {
            if (BOOKING_ERROR_TO_PANEL[bookingError]) {
                setHighlightOrderPanel({
                    id: BOOKING_ERROR_TO_PANEL[bookingError],
                    color: BOOKING_TO_PANEL_COLORS.ERROR
                });
            }

            message.error(bookingError);
            return;
        }

        const paymentData = {
            ...formValues,
            paymentType,
            ...(creditCard ? {creditCard} : {}),
            amount: totalBidAmount
        };

        log("Bid payment confirmed", paymentData);

        onSave(paymentData);
    }, [
        bookingError,
        onSave,
        formValues,
        totalBidAmount,
        creditCard,
        setHighlightOrderPanel,
        paymentType,
        log,
        bid?.price,
        bid?.deliveryPrice,
        brief?.serviceId
    ]);

    return (
        <Modal
            open={visible}
            onCancel={closeHandler}
            width={700}
            closable={true}
            maskClosable={true}
            footer={null}
            title={null}>
            <PageTitleHeader className="form-modal-header" showBack={false}>
                כמעט שם! נשאר לבחור איך לשלם כדי לסגור את ההזמנה
            </PageTitleHeader>

            <p className="form-modal-sub-title">
                <div>
                    <div style={{marginBottom: 10}}>
                        לאחר בחירת אמצעי התשלום ולחיצה על &apos;ביצוע ההזמנה&apos;, ההזמנה תועבר למערכת באופן אוטומטי
                        כאשר היא מאושרת ע״י בית העסק
                    </div>
                </div>
            </p>

            <MarketplaceBookingContext.Provider
                value={{
                    offeredPrice: bid?.price,
                    highlightOrderPanel
                }}>
                {!!product && !!bid && (
                    <ConfigProvider direction="rtl">
                        <OrderForm
                            hideSections={[
                                OrderFormPanelKeys.Location,
                                OrderFormPanelKeys.Time,
                                OrderFormPanelKeys.Amount,
                                OrderFormPanelKeys.ExtraRequests,
                                OrderFormPanelKeys.ExtraContactInfo
                            ]}
                            me={me}
                            company={company}
                            content={product}
                            formValues={formValues}
                            setFormValues={setFormValues}
                            updateFormValues={(field, val) => {
                                setFormValues(prevValues => set({...prevValues}, field, val));
                            }}
                            bookingError={bookingError}
                            providerDetails={providerDetails}
                            defaultActiveKey={activeKey}
                            onDefaultActiveKeyChange={setActiveKey}
                            log={log}
                            extraFields={
                                <div>
                                    <Collapse
                                        ghost={true}
                                        accordion={true}
                                        className={classnames("marketplace-booking-collapse", {
                                            panelError:
                                                BOOKING_ERROR_TO_PANEL[bookingError] === OrderFormPanelKeys.Payment &&
                                                highlightOrderPanel?.id === OrderFormPanelKeys.Payment
                                        })}
                                        expandIconPosition="left"
                                        activeKey={OrderFormPanelKeys.Payment}
                                        expandIcon={({isActive}) => expandIconController(isActive)}>
                                        <PaymentPanel
                                            services={services}
                                            me={me}
                                            company={company}
                                            panelKey={OrderFormPanelKeys.Payment}
                                            updateCreditCard={setCreditCard}
                                            onPaymentTypeChange={type => setPaymentType(type)}
                                            onInvoiceCommentChange={newInvoiceComment =>
                                                onFormValueChange("invoiceComment", newInvoiceComment)
                                            }
                                            invoiceComment={formValues?.invoiceComment}
                                            onInvoicePONumberChange={newPONumber =>
                                                onFormValueChange("invoicePONumber", newPONumber)
                                            }
                                            invoicePONumber={formValues?.invoicePONumber}
                                            onSeparateInvoiceChange={newVal =>
                                                onFormValueChange("separateInvoice", newVal)
                                            }
                                            budgetCategoriesPriority={formValues?.budgetCategoriesPriority}
                                            onBudgetCategoriesPriorityChange={val =>
                                                onFormValueChange("budgetCategoriesPriority", val)
                                            }
                                            separateInvoice={formValues?.separateInvoice}
                                            updateFormValues={(field, value) => onFormValueChange(field, value)}
                                            tenBisCreditCard={formValues?.tenBisCreditCard}
                                            allowedPaymentMethods={
                                                AllowedSystemIdPaymentType[getThemeByOrigin(window.location.origin)]
                                            }
                                            budgetLoadRequestAmount={totalLoadRequestBudgetAmount}
                                            log={NOOP}
                                            shouldSaveCreditCard={false}
                                        />
                                    </Collapse>
                                    <MarketplaceSeparatorLine />
                                </div>
                            }
                        />

                        <StyledButton
                            onClick={onConfirmOrderPayment}
                            style={{
                                width: "100%",
                                borderRadius: "4px",
                                color: "white",
                                backgroundColor: "var(--primary-color)"
                            }}
                            loading={isLoading}>
                            {new StringBuilder("אישור הצעה").append(totalBidAmount).append("ש״ח").toString()}
                        </StyledButton>
                    </ConfigProvider>
                )}
            </MarketplaceBookingContext.Provider>
        </Modal>
    );
};
